import React, {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import {sortAlphabetically} from '@arboxappv4/shared/src/helpers/functions';
import FiltersHandler from '../../../components/FiltersHandler';
import useScreenSize from '../../../helpers/hooks/useScreenSize';
import {getTrackActivityFiltersConfig} from '../../../helpers/functions';

const TrackActivitySelectionHeader = ({
    trackCourses,
    onFiltered,
    selectedCount,
    requiredCount,
}) => {
    const globalLocation = useSelector(state => state.site.selectedLocation);
    const [filtersConfig, setFiltersConfig] = useState(null);
    const {isMobile} = useScreenSize();

    const coursesFlat = useMemo(
        () => trackCourses?.map(course => course.series).filter(Boolean) ?? null,
        [trackCourses],
    );

    const boxCategories = useMemo(() => {
        if (coursesFlat) {
            const optionsObj = coursesFlat.reduce(
                (acc, series) => ({...acc, [`type-${series.box_category_fk}`]: series.box_categories}),
                {},
            );
            return sortAlphabetically(Object.values(optionsObj), 'name');
        }
    }, [coursesFlat]);

    const coaches = useMemo(() => {
        if (coursesFlat) {
            const optionsObj = coursesFlat.reduce((acc, series) => {
                let tempRes = acc;
                if (series.coach) {
                    tempRes = {...tempRes, [`coach-${series.coach_fk}`]: series.coach};
                }
                if (series.second_coach) {
                    tempRes = {...tempRes, [`coach-${series.second_coach_fk}`]: series.second_coach};
                }
                return tempRes;
            }, {});
            return sortAlphabetically(Object.values(optionsObj), 'full_name');
        }
    }, [coursesFlat]);

    useEffect(() => {
        if (!trackCourses) {
            return;
        }
        setFiltersConfig(getTrackActivityFiltersConfig(boxCategories, coaches, globalLocation?.id ?? 'all'));
    }, [trackCourses, boxCategories, coaches, globalLocation?.id]);

    return (
        <HeaderWrapper>
            <ControlsRow $isMobile={isMobile}>
                {!isMobile && filtersConfig &&
                    <FilterSection>
                        <FiltersHandler
                            unfilteredData={trackCourses}
                            onFiltered={onFiltered}
                            initConfig={filtersConfig}
                            pageName={'track'}
                        />
                    </FilterSection>
                }
                <SubtitleSection $isMobile={isMobile}>
                    {isMobile && <CounterPill $isMobile={isMobile}>{selectedCount}/{requiredCount}</CounterPill>}
                    <SubtitleText $isMobile={isMobile}>
                        {t('track-activity-selection-subtitle', {count: requiredCount})}
                    </SubtitleText>
                    {!isMobile && <CounterPill $isMobile={isMobile}>{selectedCount}/{requiredCount}</CounterPill>}
                </SubtitleSection>
                {isMobile && filtersConfig &&
                    <FilterSection>
                        <FiltersHandler
                            unfilteredData={trackCourses}
                            onFiltered={onFiltered}
                            initConfig={filtersConfig}
                            pageName={'track'}
                        />
                    </FilterSection>
                }
            </ControlsRow>
        </HeaderWrapper>
    );
};

export default TrackActivitySelectionHeader;

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
`;

const ControlsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${({$isMobile}) => $isMobile ? 'space-between' : 'space-between'};
    gap: 12px;
    flex-wrap: wrap;
`;

const FilterSection = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const SubtitleSection = styled.div`
    display: flex;
    align-items: center;
    gap: ${({$isMobile}) => $isMobile ? '8px' : '12px'};
    margin-inline-start: ${({$isMobile}) => $isMobile ? 0 : 'auto'};
    flex: ${({$isMobile}) => $isMobile ? 1 : 'unset'};
    min-width: 0;
`;

const SubtitleText = styled.span`
    font-size: ${({$isMobile}) => $isMobile ? '16px' : '20px'};
    font-weight: 500;
    line-height: ${({$isMobile}) => $isMobile ? '24px' : 'normal'};
    color: #212121;
    white-space: nowrap;
`;

const CounterPill = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    padding: 6px 12px;
    border-radius: 18px;
    background: #ececec;
    color: ${Colors.siteBlack};
    font-size: ${({$isMobile}) => $isMobile ? '18px' : '23px'};
    font-weight: 400;
    line-height: 1.2;
    white-space: nowrap;
`;
